import type { SqlDataset } from "@bezpart-board/shared";

export type SqlDatasetInfo = {
  id: SqlDataset;
  title: string;
  description: string;
  /** DDL + наполнение. Выполняется заново перед каждым запуском — база всегда чистая. */
  setup: string;
};

/*
 * Схемы четырёх баз повторяют учебник sql-academy.org/ru/guide: те же таблицы,
 * те же названия столбцов и те же связи. Так запрос из учебника выполняется здесь
 * без переделки. Данные свои — их меньше, чтобы результат помещался на доску.
 *
 * Отличие одно: движок здесь SQLite (sql.js), а в учебнике MySQL. Ходовые функции
 * MySQL (YEAR, TIMEDIFF, DATE_FORMAT, IF и прочие) добавлены в Worker-е,
 * см. sqlWorkerSource.ts.
 */

/**
 * Авиаперевозки: Company, Trip, Passenger, Pass_in_trip.
 * Время вылета/прилёта — текст «ЧЧ:ММ» (в учебнике это DATETIME от 1900-01-01).
 */
const AIRPORT_SETUP = `
CREATE TABLE Company (
  id   INTEGER PRIMARY KEY,
  name TEXT NOT NULL
);

CREATE TABLE Trip (
  id        INTEGER PRIMARY KEY,
  company   INTEGER NOT NULL REFERENCES Company(id),
  plane     TEXT NOT NULL,
  town_from TEXT NOT NULL,
  town_to   TEXT NOT NULL,
  time_out  TEXT NOT NULL,
  time_in   TEXT NOT NULL
);

CREATE TABLE Passenger (
  id   INTEGER PRIMARY KEY,
  name TEXT NOT NULL
);

CREATE TABLE Pass_in_trip (
  trip      INTEGER NOT NULL REFERENCES Trip(id),
  passenger INTEGER NOT NULL REFERENCES Passenger(id),
  place     TEXT NOT NULL,
  PRIMARY KEY (trip, passenger)
);

INSERT INTO Company (id, name) VALUES
  (1, 'Don_avia'),
  (2, 'Aeroflot'),
  (3, 'Dale_avia'),
  (4, 'air_France'),
  (5, 'British_AW');

INSERT INTO Trip (id, company, plane, town_from, town_to, time_out, time_in) VALUES
  (1100, 4, 'Boeing', 'Rostov',      'Paris',       '14:30', '17:50'),
  (1101, 4, 'Boeing', 'Paris',       'Rostov',      '08:12', '11:45'),
  (1123, 3, 'TU-134', 'Rostov',      'Vladivostok', '16:20', '03:40'),
  (1124, 3, 'TU-134', 'Vladivostok', 'Rostov',      '09:00', '19:50'),
  (1145, 2, 'IL-86',  'Moscow',      'Rostov',      '09:35', '11:23'),
  (1146, 2, 'IL-86',  'Rostov',      'Moscow',      '17:55', '20:01'),
  (1181, 1, 'TU-154', 'Rostov',      'Moscow',      '06:12', '08:01'),
  (1182, 1, 'TU-154', 'Moscow',      'Rostov',      '12:35', '14:30'),
  (1187, 1, 'TU-154', 'Moscow',      'Rostov',      '15:42', '17:39'),
  (1188, 1, 'TU-154', 'Rostov',      'Moscow',      '13:28', '15:11'),
  (1195, 1, 'TU-154', 'Rostov',      'Moscow',      '22:50', '00:47'),
  (1196, 1, 'TU-154', 'Moscow',      'Rostov',      '04:00', '06:00'),
  (7771, 5, 'Boeing', 'London',      'Singapore',   '01:00', '11:00'),
  (7772, 5, 'Boeing', 'Singapore',   'London',      '12:00', '23:00'),
  (7773, 5, 'Boeing', 'London',      'Singapore',   '03:00', '13:00'),
  (7774, 5, 'Boeing', 'Singapore',   'London',      '15:00', '01:00'),
  (7775, 5, 'Boeing', 'London',      'Singapore',   '09:00', '20:00'),
  (7776, 5, 'Boeing', 'Singapore',   'London',      '20:00', '06:00'),
  (7778, 5, 'Boeing', 'Paris',       'London',      '10:00', '11:00'),
  (7895, 3, 'Airbus', 'Moscow',      'Praha',       '07:15', '09:05'),
  (8881, 5, 'Boeing', 'London',      'Paris',       '03:00', '04:00'),
  (8882, 5, 'Boeing', 'Paris',       'London',      '19:00', '20:00');

INSERT INTO Passenger (id, name) VALUES
  (1,  'Bruce Willis'),
  (2,  'George Clooney'),
  (3,  'Kevin Costner'),
  (4,  'Donald Sutherland'),
  (5,  'Jennifer Lopez'),
  (6,  'Ray Liotta'),
  (7,  'Samuel Jackson'),
  (8,  'Nikole Kidman'),
  (9,  'Alan Rickman'),
  (10, 'Michael Caine'),
  (11, 'Steve Martin'),
  (12, 'Sylvester Stallone'),
  (13, 'Mel Gibson'),
  (14, 'Sigourney Weaver'),
  (15, 'Gary Oldman'),
  (16, 'Pierce Brosnan');

INSERT INTO Pass_in_trip (trip, passenger, place) VALUES
  (1100, 1,  '1a'),
  (1100, 3,  '2a'),
  (1100, 5,  '4c'),
  (1101, 1,  '4a'),
  (1101, 6,  '4b'),
  (1123, 2,  '2d'),
  (1123, 4,  '2a'),
  (1123, 7,  '4b'),
  (1124, 2,  '1b'),
  (1145, 8,  '3c'),
  (1145, 9,  '3d'),
  (1146, 8,  '1a'),
  (1181, 10, '1a'),
  (1181, 11, '1b'),
  (1182, 10, '2c'),
  (1187, 12, '5a'),
  (1188, 12, '5b'),
  (1188, 13, '5c'),
  (1195, 14, '1d'),
  (1196, 14, '2d'),
  (7771, 1,  '1a'),
  (7771, 15, '1b'),
  (7772, 15, '2b'),
  (7773, 9,  '3a'),
  (7775, 5,  '4d'),
  (7776, 5,  '4a'),
  (7778, 3,  '2b'),
  (8881, 13, '1c'),
  (8882, 13, '1d');
`;

/** Семейный бюджет: FamilyMembers, GoodTypes, Goods, Payments. */
const FAMILY_SETUP = `
CREATE TABLE FamilyMembers (
  member_id   INTEGER PRIMARY KEY,
  status      TEXT NOT NULL,
  member_name TEXT NOT NULL,
  birthday    TEXT NOT NULL
);

CREATE TABLE GoodTypes (
  good_type_id   INTEGER PRIMARY KEY,
  good_type_name TEXT NOT NULL
);

CREATE TABLE Goods (
  good_id   INTEGER PRIMARY KEY,
  good_name TEXT NOT NULL,
  type      INTEGER NOT NULL REFERENCES GoodTypes(good_type_id)
);

CREATE TABLE Payments (
  payment_id    INTEGER PRIMARY KEY,
  family_member INTEGER NOT NULL REFERENCES FamilyMembers(member_id),
  good          INTEGER NOT NULL REFERENCES Goods(good_id),
  date          TEXT NOT NULL,
  unit_price    INTEGER NOT NULL,
  amount        INTEGER NOT NULL
);

INSERT INTO FamilyMembers (member_id, status, member_name, birthday) VALUES
  (1, 'father',   'Headley Quincey',   '1960-05-13'),
  (2, 'mother',   'Flavia Quincey',    '1963-02-16'),
  (3, 'son',      'Andie Quincey',     '1983-06-05'),
  (4, 'daughter', 'Lela Quincey',      '1985-06-07'),
  (5, 'daughter', 'Annie Quincey',     '1988-04-10'),
  (6, 'father',   'Ernest Forrest',    '1961-09-11'),
  (7, 'mother',   'Constance Forrest', '1968-09-06');

INSERT INTO GoodTypes (good_type_id, good_type_name) VALUES
  (1, 'food'),
  (2, 'entertainment'),
  (3, 'medicine'),
  (4, 'transport'),
  (5, 'clothes'),
  (6, 'delicacies'),
  (7, 'furniture');

INSERT INTO Goods (good_id, good_name, type) VALUES
  (1,  'apartment fee',  4),
  (2,  'phone bill',     4),
  (3,  'bread',          1),
  (4,  'milk',           1),
  (5,  'potato',         1),
  (6,  'tea',            1),
  (7,  'red caviar',     6),
  (8,  'black caviar',   6),
  (9,  'cinema ticket',  2),
  (10, 'theatre',        2),
  (11, 'video game',     2),
  (12, 'tooth paste',    3),
  (13, 'cough medicine', 3),
  (14, 'vitamins',       3),
  (15, 'bus ticket',     4),
  (16, 'jeans',          5),
  (17, 'jacket',         5),
  (18, 'sneakers',       5),
  (19, 'book',           2),
  (20, 'lamp',           7);

INSERT INTO Payments (payment_id, family_member, good, date, unit_price, amount) VALUES
  (1,  1, 1,  '2005-01-20', 5400, 1),
  (2,  1, 2,  '2005-01-20', 470,  1),
  (3,  2, 3,  '2005-01-21', 21,   4),
  (4,  2, 4,  '2005-01-21', 34,   3),
  (5,  3, 9,  '2005-01-22', 250,  2),
  (6,  4, 9,  '2005-01-22', 250,  1),
  (7,  2, 7,  '2005-02-04', 320,  2),
  (8,  2, 8,  '2005-02-04', 980,  1),
  (9,  1, 5,  '2005-02-07', 18,   10),
  (10, 5, 12, '2005-02-08', 72,   2),
  (11, 5, 14, '2005-02-08', 260,  1),
  (12, 3, 16, '2005-02-12', 1200, 1),
  (13, 4, 18, '2005-02-12', 2300, 1),
  (14, 2, 17, '2005-02-15', 3100, 1),
  (15, 1, 15, '2005-03-01', 15,   20),
  (16, 3, 11, '2005-03-03', 890,  1),
  (17, 4, 19, '2005-03-05', 340,  2),
  (18, 2, 3,  '2005-03-06', 23,   6),
  (19, 2, 4,  '2005-03-06', 36,   4),
  (20, 1, 1,  '2005-03-20', 5400, 1),
  (21, 1, 2,  '2005-03-20', 510,  1),
  (22, 5, 13, '2005-03-22', 145,  2),
  (23, 3, 10, '2005-03-25', 700,  2),
  (24, 4, 6,  '2005-03-28', 95,   3),
  (25, 3, 7,  '2005-04-02', 340,  1);
`;

/** Школа: Class, Student, Student_in_class, Subject, Teacher, Timepair, Schedule. */
const SCHOOL_SETUP = `
CREATE TABLE Class (
  id   INTEGER PRIMARY KEY,
  name TEXT NOT NULL
);

CREATE TABLE Student (
  id          INTEGER PRIMARY KEY,
  first_name  TEXT NOT NULL,
  middle_name TEXT NOT NULL,
  last_name   TEXT NOT NULL,
  birthday    TEXT NOT NULL,
  address     TEXT NOT NULL
);

CREATE TABLE Student_in_class (
  id      INTEGER PRIMARY KEY,
  class   INTEGER NOT NULL REFERENCES Class(id),
  student INTEGER NOT NULL REFERENCES Student(id)
);

CREATE TABLE Subject (
  id   INTEGER PRIMARY KEY,
  name TEXT NOT NULL
);

CREATE TABLE Teacher (
  id          INTEGER PRIMARY KEY,
  first_name  TEXT NOT NULL,
  middle_name TEXT NOT NULL,
  last_name   TEXT NOT NULL
);

CREATE TABLE Timepair (
  id         INTEGER PRIMARY KEY,
  start_pair TEXT NOT NULL,
  end_pair   TEXT NOT NULL
);

CREATE TABLE Schedule (
  id          INTEGER PRIMARY KEY,
  date        TEXT NOT NULL,
  class       INTEGER NOT NULL REFERENCES Class(id),
  number_pair INTEGER NOT NULL REFERENCES Timepair(id),
  teacher     INTEGER NOT NULL REFERENCES Teacher(id),
  subject     INTEGER NOT NULL REFERENCES Subject(id),
  classroom   INTEGER NOT NULL
);

INSERT INTO Class (id, name) VALUES
  (1, '10 A'),
  (2, '10 B'),
  (3, '11 A'),
  (4, '11 B'),
  (5, '9 A');

INSERT INTO Student (id, first_name, middle_name, last_name, birthday, address) VALUES
  (1,  'Grigorij',   'Gennadevich',   'Kapustin',     '2001-12-13', 'ul. Pervomajskaya, d. 45, kv. 6'),
  (2,  'Anna',       'Ivanovna',      'Osipova',      '2002-03-04', 'ul. Pushkina, d. 15, kv. 5'),
  (3,  'Anatolij',   'Petrovich',     'Zhdanov',      '2007-07-15', 'ul. CHernova, d. 9, kv. 34'),
  (4,  'Angelina',   'Sergeevna',     'Voroncova',    '2002-09-21', 'ul. Pushkina, d. 3, kv. 12'),
  (5,  'Arina',      'Olegovna',      'Evseeva',      '2003-01-08', 'ul. Lesnaya, d. 21, kv. 3'),
  (6,  'Anfisa',     'Dmitrievna',    'Kalashnikova', '2002-11-30', 'pr. Mira, d. 7, kv. 41'),
  (7,  'Aleksandra', 'Pavlovna',      'Belozyorova',  '2004-05-19', 'ul. Sadovaya, d. 12, kv. 8'),
  (8,  'Angelina',   'Igorevna',      'Lazareva',     '2004-08-02', 'ul. Pushkina, d. 15, kv. 44'),
  (9,  'Anna',       'Nikolaevna',    'Kulagina',     '2003-04-27', 'ul. Zelenaya, d. 5, kv. 17'),
  (10, 'Arina',      'Antonovna',     'Sharapova',    '2005-02-14', 'pr. Lenina, d. 88, kv. 2'),
  (11, 'Angelina',   'Vitalevna',     'Lukina',       '2005-10-06', 'ul. Lesnaya, d. 4, kv. 9'),
  (12, 'Boris',      'Vladimirovich', 'Nikitin',      '2001-06-25', 'ul. Sadovaya, d. 30, kv. 15'),
  (13, 'Viktor',     'Alekseevich',   'Panov',        '2006-12-01', 'pr. Mira, d. 19, kv. 7'),
  (14, 'Darya',      'Maksimovna',    'Frolova',      '2006-09-09', 'ul. Pushkina, d. 15, kv. 61'),
  (15, 'Egor',       'Romanovich',    'Tarasov',      '2007-03-17', 'ul. Zelenaya, d. 11, kv. 28');

INSERT INTO Student_in_class (id, class, student) VALUES
  (1,  1, 1),
  (2,  1, 2),
  (3,  1, 4),
  (4,  1, 6),
  (5,  2, 5),
  (6,  2, 9),
  (7,  2, 12),
  (8,  3, 7),
  (9,  3, 8),
  (10, 3, 11),
  (11, 4, 10),
  (12, 4, 14),
  (13, 5, 3),
  (14, 5, 13),
  (15, 5, 15);

INSERT INTO Subject (id, name) VALUES
  (1, 'Mathematics'),
  (2, 'Physics'),
  (3, 'Chemistry'),
  (4, 'Biology'),
  (5, 'Physical Culture'),
  (6, 'Literature'),
  (7, 'History'),
  (8, 'Informatics');

INSERT INTO Teacher (id, first_name, middle_name, last_name) VALUES
  (1, 'Petr',     'Petrovich',  'Romashkin'),
  (2, 'Irina',    'Sergeevna',  'Kuzmina'),
  (3, 'Oleg',     'Ivanovich',  'Krauze'),
  (4, 'Marina',   'Andreevna',  'Zueva'),
  (5, 'Sergej',   'Olegovich',  'Vinogradov'),
  (6, 'Elena',    'Borisovna',  'Sokolova'),
  (7, 'Vladimir', 'Yurevich',   'Nesterov'),
  (8, 'Nikolaj',  'Ivanovich',  'Semenov');

INSERT INTO Timepair (id, start_pair, end_pair) VALUES
  (1, '08:30', '09:15'),
  (2, '09:25', '10:10'),
  (3, '10:25', '11:10'),
  (4, '11:20', '12:05'),
  (5, '12:25', '13:10'),
  (6, '13:20', '14:05'),
  (7, '14:15', '15:00');

INSERT INTO Schedule (id, date, class, number_pair, teacher, subject, classroom) VALUES
  (1,  '2019-09-02', 1, 1, 1, 1, 301),
  (2,  '2019-09-02', 1, 2, 2, 6, 205),
  (3,  '2019-09-02', 1, 3, 3, 2, 310),
  (4,  '2019-09-02', 2, 1, 4, 3, 214),
  (5,  '2019-09-02', 2, 2, 1, 1, 301),
  (6,  '2019-09-02', 3, 1, 5, 5, 101),
  (7,  '2019-09-02', 3, 2, 6, 4, 208),
  (8,  '2019-09-02', 4, 3, 7, 7, 220),
  (9,  '2019-09-03', 1, 1, 5, 5, 101),
  (10, '2019-09-03', 1, 2, 4, 3, 214),
  (11, '2019-09-03', 2, 1, 2, 6, 205),
  (12, '2019-09-03', 2, 3, 3, 2, 310),
  (13, '2019-09-03', 3, 1, 1, 1, 301),
  (14, '2019-09-03', 3, 4, 7, 7, 220),
  (15, '2019-09-03', 5, 1, 6, 4, 208),
  (16, '2019-09-04', 1, 1, 1, 1, 301),
  (17, '2019-09-04', 1, 3, 7, 7, 220),
  (18, '2019-09-04', 2, 2, 5, 5, 101),
  (19, '2019-09-04', 3, 2, 3, 2, 310),
  (20, '2019-09-04', 4, 1, 2, 6, 205),
  (21, '2019-09-04', 4, 2, 1, 1, 301),
  (22, '2019-09-05', 1, 1, 6, 4, 208),
  (23, '2019-09-05', 2, 1, 1, 1, 301),
  (24, '2019-09-05', 3, 3, 5, 5, 101),
  (25, '2019-09-05', 5, 2, 2, 6, 205),
  (26, '2019-09-06', 1, 2, 3, 2, 310),
  (27, '2019-09-06', 3, 1, 4, 3, 214),
  (28, '2019-09-06', 4, 1, 5, 5, 101);
`;

/** Аренда жилья: Users, Rooms, Reservations, Reviews — основная база учебника. */
const AIRBNB_SETUP = `
CREATE TABLE Users (
  id                INTEGER PRIMARY KEY,
  name              TEXT NOT NULL,
  email             TEXT NOT NULL,
  email_verified_at TEXT,
  password          TEXT NOT NULL,
  phone_number      TEXT NOT NULL
);

CREATE TABLE Rooms (
  id           INTEGER PRIMARY KEY,
  home_type    TEXT NOT NULL,
  address      TEXT NOT NULL,
  has_tv       INTEGER NOT NULL,
  has_internet INTEGER NOT NULL,
  has_kitchen  INTEGER NOT NULL,
  has_air_con  INTEGER NOT NULL,
  price        INTEGER NOT NULL,
  owner_id     INTEGER NOT NULL REFERENCES Users(id),
  latitude     REAL NOT NULL,
  longitude    REAL NOT NULL
);

CREATE TABLE Reservations (
  id         INTEGER PRIMARY KEY,
  user_id    INTEGER NOT NULL REFERENCES Users(id),
  room_id    INTEGER NOT NULL REFERENCES Rooms(id),
  start_date TEXT NOT NULL,
  end_date   TEXT NOT NULL,
  price      INTEGER NOT NULL,
  total      INTEGER NOT NULL
);

CREATE TABLE Reviews (
  id             INTEGER PRIMARY KEY,
  reservation_id INTEGER NOT NULL REFERENCES Reservations(id),
  rating         INTEGER NOT NULL
);

INSERT INTO Users (id, name, email, email_verified_at, password, phone_number) VALUES
  (1,  'George Clooney',  'clooney@gmail.com',   '2016-03-12 10:04:11', 'a7fk20dlm3qz', '+7 495 111 2233'),
  (2,  'Bruce Willis',    'wainwrig@msn.com',    '2016-11-19 12:30:43', 'fzjhl0v82o0a', '+1 202 555 0176'),
  (3,  'Johnny Depp',     'cgarcia@yahoo.ca',    '2017-05-26 01:19:06', 'qpp6hbnae42c', '+7 401 195 7363'),
  (4,  'Anna Kern',       'anna.kern@mail.ru',   NULL,                  'k1s8dnc7v0lq', '+375 29 764 1102'),
  (5,  'Ivan Petrov',     'ivan.petrov@mail.ru', '2018-01-14 08:45:00', 'zz91mbq4xtro', '+7 812 400 5566'),
  (6,  'Marina Belova',   'belova@outlook.com',  '2018-07-30 17:22:31', 'w4ptn6sd1ekm', '+375 33 210 8899'),
  (7,  'John Travolta',   'travolta@msn.com',    '2019-02-02 09:10:55', 'h2gkl8eq7aov', '+1 310 555 0142'),
  (8,  'Olga Smirnova',   'olga.s@icloud.com',   NULL,                  'r5cyt3zn9uwx', '+7 495 777 0101'),
  (9,  'Pavel Orlov',     'orlov@gmail.com',     '2019-09-18 14:03:27', 'm8vqd2jf6bpl', '+7 921 330 4477'),
  (10, 'Kate Winslet',    'kate.w@outlook.com',  '2020-04-05 11:52:09', 'x3nwu7ka0tsc', '+44 20 7946 0958'),
  (11, 'Sergej Nikolaev', 'nikolaev@yandex.ru',  '2020-10-23 19:37:44', 'b6lzp4ye8hqn', '+375 25 601 3344'),
  (12, 'Elena Morozova',  'morozova@mail.ru',    NULL,                  'j9strk1o5dvm', '+7 863 250 6070');

INSERT INTO Rooms (id, home_type, address, has_tv, has_internet, has_kitchen, has_air_con, price, owner_id, latitude, longitude) VALUES
  (1,  'Private room',    '11218, Friel Place, New York',        1, 1, 0, 0, 1400, 1,  40.6432, -73.9750),
  (2,  'Entire home/apt', '10036, West 45th Street, New York',   1, 1, 1, 1, 5200, 1,  40.7590, -73.9880),
  (3,  'Shared room',     '94103, Folsom Street, San Francisco', 0, 1, 1, 0, 900,  3,  37.7749, -122.4130),
  (4,  'Entire home/apt', '75001, Rue de Rivoli, Paris',         1, 1, 1, 1, 6100, 5,  48.8620, 2.3360),
  (5,  'Private room',    '10115, Torstrasse, Berlin',           0, 1, 1, 0, 2100, 5,  52.5300, 13.4020),
  (6,  'Entire home/apt', '119021, ul. Ostozhenka, Moscow',      1, 1, 1, 1, 4800, 6,  55.7400, 37.5950),
  (7,  'Private room',    '191186, Nevsky pr., Saint-Petersburg', 1, 0, 1, 0, 1900, 9, 59.9350, 30.3250),
  (8,  'Shared room',     '08001, Carrer de Sants, Barcelona',   0, 0, 1, 1, 1100, 10, 41.3750, 2.1340),
  (9,  'Entire home/apt', 'SW1A, Buckingham Gate, London',       1, 1, 1, 1, 7300, 10, 51.4990, -0.1420),
  (10, 'Private room',    '00184, Via Cavour, Rome',             1, 1, 0, 1, 2600, 11, 41.8950, 12.4920),
  (11, 'Shared room',     '220030, pr. Nezavisimosti, Minsk',    0, 1, 0, 0, 800,  11, 53.9020, 27.5620),
  (12, 'Entire home/apt', '10036, West 44th Street, New York',   1, 1, 1, 0, 4400, 1,  40.7570, -73.9860);

INSERT INTO Reservations (id, user_id, room_id, start_date, end_date, price, total) VALUES
  (1,  2,  1,  '2021-03-01', '2021-03-05', 1400, 5600),
  (2,  3,  1,  '2021-04-10', '2021-04-12', 1400, 2800),
  (3,  4,  2,  '2021-04-15', '2021-04-20', 5200, 26000),
  (4,  7,  2,  '2021-06-01', '2021-06-03', 5200, 10400),
  (5,  8,  3,  '2021-06-07', '2021-06-14', 900,  6300),
  (6,  2,  4,  '2021-07-02', '2021-07-09', 6100, 42700),
  (7,  9,  4,  '2021-08-11', '2021-08-13', 6100, 12200),
  (8,  10, 5,  '2021-08-20', '2021-08-25', 2100, 10500),
  (9,  12, 6,  '2021-09-01', '2021-09-04', 4800, 14400),
  (10, 3,  6,  '2021-09-15', '2021-09-16', 4800, 4800),
  (11, 5,  7,  '2021-10-05', '2021-10-12', 1900, 13300),
  (12, 6,  7,  '2021-11-01', '2021-11-03', 1900, 3800),
  (13, 8,  9,  '2021-11-20', '2021-11-27', 7300, 51100),
  (14, 11, 9,  '2021-12-03', '2021-12-05', 7300, 14600),
  (15, 2,  10, '2022-01-08', '2022-01-14', 2600, 15600),
  (16, 4,  10, '2022-02-11', '2022-02-13', 2600, 5200),
  (17, 7,  12, '2022-03-02', '2022-03-09', 4400, 30800),
  (18, 9,  12, '2022-03-20', '2022-03-22', 4400, 8800),
  (19, 12, 1,  '2022-04-01', '2022-04-08', 1400, 9800),
  (20, 5,  5,  '2022-05-05', '2022-05-07', 2100, 4200);

INSERT INTO Reviews (id, reservation_id, rating) VALUES
  (1,  1,  5),
  (2,  2,  4),
  (3,  3,  5),
  (4,  4,  3),
  (5,  5,  2),
  (6,  6,  5),
  (7,  7,  4),
  (8,  8,  4),
  (9,  9,  5),
  (10, 10, 3),
  (11, 11, 4),
  (12, 13, 5),
  (13, 14, 5),
  (14, 15, 2),
  (15, 17, 4),
  (16, 18, 3),
  (17, 19, 5);
`;

/** Пустая база: преподаватель сам пишет CREATE TABLE и INSERT. */
export const CUSTOM_SETUP_DEFAULT = `-- Своя база: пишите здесь CREATE TABLE и INSERT.
-- Скрипт выполняется заново перед каждым запуском — база всегда чистая.

CREATE TABLE Students (
  id    INTEGER PRIMARY KEY,
  name  TEXT NOT NULL,
  class TEXT NOT NULL,
  grade INTEGER
);

INSERT INTO Students (id, name, class, grade) VALUES
  (1, 'Аня',   '9А', 5),
  (2, 'Борис', '9А', 4),
  (3, 'Вера',  '9Б', 5),
  (4, 'Глеб',  '9Б', 3);
`;

export const SQL_DATASETS: SqlDatasetInfo[] = [
  {
    id: "airbnb",
    title: "Аренда жилья",
    description: "Users, Rooms, Reservations, Reviews — основная база учебника sql-academy",
    setup: AIRBNB_SETUP,
  },
  {
    id: "family",
    title: "Семейный бюджет",
    description: "FamilyMembers, GoodTypes, Goods, Payments — вторая база учебника",
    setup: FAMILY_SETUP,
  },
  {
    id: "airport",
    title: "Авиаперевозки",
    description: "Company, Trip, Passenger, Pass_in_trip — соединения и время",
    setup: AIRPORT_SETUP,
  },
  {
    id: "school",
    title: "Школа",
    description: "Class, Student, Student_in_class, Subject, Teacher, Timepair, Schedule",
    setup: SCHOOL_SETUP,
  },
  {
    id: "custom",
    title: "Своя база",
    description: "Пустая база: CREATE TABLE и INSERT пишете сами",
    setup: "",
  },
];

export function datasetInfo(id: SqlDataset): SqlDatasetInfo {
  return SQL_DATASETS.find((d) => d.id === id) ?? SQL_DATASETS[0];
}

/** SQL подготовки базы; для «своей базы» — текст преподавателя. */
export function datasetSetupSql(id: SqlDataset, customSetup?: string): string {
  if (id === "custom") return customSetup ?? CUSTOM_SETUP_DEFAULT;
  return datasetInfo(id).setup;
}
